import React, { useCallback, useEffect } from 'react';
import { Box, IconButton, Typography } from "@mui/material";
import { FileDownload as FileDownloadIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import { MaterialReactTable } from 'material-react-table';
import Events from '../../../events';
import { EventsType } from '../../../events-types';
import {
    getByFilters,
    getObjectsByRegEx,
    TOXICANTS_DISEASE,
} from '../../../api/Compounds';
import { iBaseCardWithComponentProps, iBaseInnerCardProps } from "../../types";
import Button from "@mui/material/Button";
import { CardWithBaseComponent } from "../../common/BaseCard";
import { CellEventMapping, createCellsWithEvents, getColumns, getInitialColumnState } from "../../../utils/tables";
import { OnChangeFn, ColumnFiltersState } from "@tanstack/react-table";
import { exportToExcel, exportToExcelSelectedRows } from "../../../utils/export_xlsx";
import { ToxicantsDisease} from "./types";
import {ProductAnalysisValue} from "../products/types";

const _ToxicantsDiseasesTableCard: React.FC<iBaseInnerCardProps> = ({
                                                                        data,
                                                                        setColumnFilters,
                                                                        columnFilters,
                                                                        isActive,
                                                                        searchListBy,
                                                                        searchListByFilters
                                                                    }) => {
    const UUID = '_ToxicantsDiseasesTableCard';

    const dynamicFields = { COMPOUND_NAME: 'string', CAS: 'string' };
    const extendedToxicantsDisease = { ...ToxicantsDisease, ...dynamicFields };
    const columnNames = Object.keys(extendedToxicantsDisease);
    const columnsToView = ['INCHIKEY', 'CAS', 'COMPOUND_NAME', 'DISEASE_CATEGORY'];

    const eventMapping: CellEventMapping = {
        'INCHIKEY': EventsType.SEARCH_BY_INCHIKEY,
        'CAS': EventsType.SEARCH_BY_CAS
    };

    const columns = getColumns(extendedToxicantsDisease, 0, createCellsWithEvents(eventMapping));
    const _initial_columns_state = getInitialColumnState(columnNames, columnsToView);

    // SEARCH -------------

    const _search = useCallback((text: string) => {
        if (text.length > 0) {
            searchListBy?.(getObjectsByRegEx, TOXICANTS_DISEASE,
                'INCHIKEY,DISEASE_CATEGORY,COMMENT', text);
        }
    }, [searchListBy]);

    const searchEvent = useCallback((event: { detail: string }) => {
        _search(event.detail);
    }, [_search]);

    const searchWizard = useCallback((event: { detail: { component: string; filters: any[]; } }) => {
        if (event.detail.component === TOXICANTS_DISEASE) {
            searchListByFilters?.(getByFilters, TOXICANTS_DISEASE, event.detail.filters, 'wizard result');
        }
    }, [searchListByFilters]);

    const searchByInchiKeyEvent = useCallback((event: { detail: string }) => {
        searchListBy?.(getObjectsByRegEx, TOXICANTS_DISEASE, 'INCHIKEY', event.detail);
    }, [searchListBy]);

    // EFFECTS
    useEffect(() => {
        if (isActive) {
            Events.on(EventsType.SEARCH, searchEvent, UUID);
            Events.on(EventsType.SEARCH_WIZARD, searchWizard, UUID);
            Events.on(EventsType.SEARCH_BY_INCHIKEY, searchByInchiKeyEvent, UUID);
        }
        return () => {
            Events.off(EventsType.SEARCH, UUID);
            Events.off(EventsType.SEARCH_WIZARD, UUID);
            Events.off(EventsType.SEARCH_BY_INCHIKEY, UUID);
        };
    }, [isActive, searchEvent, searchWizard, searchByInchiKeyEvent]);

    // RENDER ----------------------

    const handleFilterFnChange: OnChangeFn<ColumnFiltersState> = (filters: any) => {
        setColumnFilters(filters);
    };

    return (
        <MaterialReactTable
            columns={columns}
            data={data ?? []}
            enableRowSelection
            enableColumnOrdering
            enableGlobalFilter={false}
            enableDensityToggle={true}
            initialState={{
                columnVisibility: _initial_columns_state,
                density: 'compact',
                columnOrder: [
                    'mrt-row-actions',
                    'mrt-row-select',
                    'mrt-row-expand',
                    'INCHIKEY',
                    'CAS',
                    'COMPOUND_NAME',
                    'DISEASE_CATEGORY',
                ],
            }}
            renderDetailPanel={({ row }) => (
                <Box
                    sx={{
                        display: 'grid',
                        margin: 'auto',
                        gridTemplateColumns: '1fr 1fr',
                        width: '100%',
                    }}
                >
                    <Typography><b>Comment:</b> {row.original.COMMENT}</Typography>
                    {row.original.LINK &&
                        row.original.LINK.trim() !== '' &&
                        row.original.LINK.split(';').map((link: string, index: number) => {
                            const trimmedLink = link.trim();
                            if (!trimmedLink) return null;
                            return (
                                <Typography key={index}>
                                    <b>SOURCE {index + 1}:</b>{' '}
                                    <a href={trimmedLink} target="_blank" rel="noopener noreferrer">
                                        {trimmedLink}
                                    </a>
                                </Typography>
                            );
                        })}


                </Box>
            )}
            onColumnFiltersChange={handleFilterFnChange}
            state={{ columnFilters }}
            enableRowActions={true}
            renderRowActions={({ row }) => (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                    <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => {
                            Events.trigger(EventsType.SEARCH_BY_TOXICANTS_DISEASE, row.original);
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </Box>
            )}
            positionToolbarAlertBanner="bottom"
            renderTopToolbarCustomActions={({ table }) => (
                <Box
                    sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}
                >
                    <Button
                        color="primary"
                        onClick={() => exportToExcel(data, "ToxicantsDiseases")}
                        startIcon={<FileDownloadIcon />}
                        variant="contained"
                    >
                        Export All Data
                    </Button>
                    <Button
                        disabled={
                            !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                        }
                        onClick={() => exportToExcelSelectedRows(table.getSelectedRowModel().rows, "ToxicantsDiseases")}
                        startIcon={<FileDownloadIcon />}
                        variant="contained"
                    >
                        Export Selected Rows
                    </Button>
                </Box>
            )}
        />
    );
};

const ToxicantsDiseasesTableCard: React.FC<Omit<iBaseCardWithComponentProps, 'cardComponent'>> = ({ header, width, eventGroupToTrigger }) => {
    return <CardWithBaseComponent cardComponent={_ToxicantsDiseasesTableCard} header={header} width={width} eventGroupToTrigger={eventGroupToTrigger} />;
};

export default React.memo(ToxicantsDiseasesTableCard);
